import { IconCheck } from '@developerz/ui';
import { createEffect, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';

// The funnel's step rail — the canvas ONBOARDING artboard's left panel. A
// presenter: the container passes the i18n step labels and the current index,
// and the SERVER drives that index (`STATE_STEP`), so nothing here is clickable.
//
//   done      a shipped-green ✓ in its box, muted label
//   current   the live plate and an accent-bordered box — the one step in
//             progress (`views/_onboarding.scss`, a live-surface row of
//             `scripts/accent-discipline.test.ts`), `aria-current="step"`
//   future    an empty bordered box, muted label
//
// Status is ALSO carried by visually-hidden text, never by the box alone: the
// three states used to differ only in colour, so a screen-reader user heard nine
// identical items (WCAG 1.4.1).
//
// "You can close this. It reopens exactly here." is TRUE, which is why it ships:
// the step is `OnboardingState` on the account, read from
// `GET /v1/onboarding/state`, never local storage.
//
// Below `lg` the list is a horizontal scroll strip, and the current step is
// scrolled into view inside it. The strip is a scroll port, so it is a named,
// focusable region a keyboard can scroll (the `<ScrollPort/>` rule).

export type OnboardingStepsProps = {
  readonly labels: readonly string[];
  /** Index of the active step. `>= labels.length` means every step is done. */
  readonly current: number;
};

type StepStatus = 'done' | 'current' | 'future';

const STATUS_KEY: Readonly<Record<StepStatus, string>> = {
  done: 'onboarding.step_state_done',
  current: 'onboarding.step_state_active',
  future: 'onboarding.step_state_upcoming',
};

export function OnboardingSteps(props: OnboardingStepsProps): JSX.Element {
  let strip: HTMLOListElement | undefined;
  const statusOf = (i: number): StepStatus =>
    i < props.current ? 'done' : i === props.current ? 'current' : 'future';

  // Keep the step in progress visible in the phone strip. Horizontal only, and
  // on the strip itself: `scrollIntoView` would also scroll the PAGE.
  createEffect(() => {
    const at = props.current;
    const item = strip?.children.item(at) as HTMLElement | null | undefined;
    if (!strip || !item || strip.scrollWidth <= strip.clientWidth) return;
    strip.scrollLeft = item.offsetLeft - (strip.clientWidth - item.offsetWidth) / 2;
  });

  return (
    <aside class="onboarding-rail" aria-labelledby="onboarding-rail-kicker">
      <p id="onboarding-rail-kicker" class="onboarding-rail__kicker">
        {t('onboarding.rail_kicker')}
      </p>
      <ol
        ref={strip}
        class="onboarding-rail__steps"
        aria-label={t('onboarding.steps_label')}
        tabindex="0"
      >
        <For each={props.labels}>
          {(key, i) => (
            <li
              class="onboarding-rail__step"
              classList={{
                'onboarding-rail__step--done': statusOf(i()) === 'done',
                'onboarding-rail__step--live': statusOf(i()) === 'current',
              }}
              aria-current={statusOf(i()) === 'current' ? 'step' : undefined}
            >
              <span
                class="onboarding-rail__mark"
                classList={{
                  'onboarding-rail__mark--done': statusOf(i()) === 'done',
                  'onboarding-rail__mark--live': statusOf(i()) === 'current',
                }}
                aria-hidden="true"
              >
                <Show when={statusOf(i()) === 'done'}>
                  <IconCheck size="0.625rem" />
                </Show>
              </span>
              <span class="sr-only">{t(STATUS_KEY[statusOf(i())])}</span>
              {t(key)}
            </li>
          )}
        </For>
      </ol>
      <p class="onboarding-rail__resume">{t('onboarding.rail_resume')}</p>
    </aside>
  );
}
